/**
 * Wires the editable regions inside a `freeform` block's `v-html` output when the
 * page is in builder edit mode. A directive can't reach into `v-html`, so this
 * scans the rendered container for the `data-edit*` annotations and:
 *   - `[data-edit]`        → contenteditable (plain text, or innerHTML when the
 *                            field's declared type is `rich_text`), committed via
 *                            the bridge's `emitInlineEdit(index, key, value)`.
 *   - `[data-edit-img]` /  → flagged as an editable region (visual affordance);
 *     `[data-edit-link]`     the swap/link-edit UX is layered on next.
 *
 * Re-runs whenever the block's html changes (a streamed `blocks:set` re-render)
 * and on unmount tears everything down. No-ops on the published site.
 */
import { onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import { getActiveBridge } from "~/composables/useEditModeBridge";
import { attachInlineEditable } from "~/composables/useInlineEdit";

export function useFreeformEditable(
  container,
  fields,
  htmlSignal,
) {
  let cleanups = [];

  function teardown() {
    for (const fn of cleanups) fn();
    cleanups = [];
  }

  function wire() {
    teardown();
    const bridge = getActiveBridge();
    const el = container.value;
    if (!bridge || !bridge.editable || !el) return;

    const typeByKey = {};
    for (const f of fields.value ?? []) typeByKey[f.key] = f.type;

    el.querySelectorAll("[data-edit]").forEach((node) => {
      const key = node.getAttribute("data-edit") || "";
      if (!key) return;
      const isRich = typeByKey[key] === "rich_text";
      node.classList.add("hk-editable-region");
      cleanups.push(attachInlineEditable(node, key, { html: isRich }));
    });

    el.querySelectorAll("[data-edit-img],[data-edit-link]").forEach((node) => {
      node.classList.add("hk-editable-region");
      const isImg = node.hasAttribute("data-edit-img");
      const key = node.getAttribute(isImg ? "data-edit-img" : "data-edit-link") || "";
      if (!key) return;
      const onClick = (ev) => {
        ev.preventDefault();
        ev.stopPropagation(); // beat EditableBlock's select-on-click
        const host = node.closest("[data-block-index]");
        const index = host ? Number(host.getAttribute("data-block-index")) : NaN;
        if (Number.isNaN(index)) return;
        if (isImg) bridge.emitEditImage(index, key);
        else bridge.emitEditLink(index, key);
      };
      node.addEventListener("click", onClick);
      cleanups.push(() => {
        node.removeEventListener("click", onClick);
        node.classList.remove("hk-editable-region");
      });
    });
  }

  onMounted(() => nextTick(wire));
  watch(htmlSignal, () => nextTick(wire));
  onBeforeUnmount(teardown);
}
